import React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import { GenerateButton } from '../GenerateButton';
import { Copy, BriefArea, Hint } from './BriefModal.styles';

/**
 * The shortest brief worth sending, in characters.
 *
 * A word count would be the obvious measure, but "casino bonuses" is two words
 * and enough to write from, while "write about it" is three and is not. Length
 * is the cruder measure and the harder one to satisfy by accident: 24 characters
 * is about four ordinary words, which is where a brief stops being a shrug and
 * starts naming a subject. The writer is told to follow the brief closely, so a
 * thin one produces a thin article rather than an error.
 */
const MIN_BRIEF_CHARS = 24;

export type BriefModalProps = {
  open: boolean;
  /** Dialog heading, e.g. "Write this article". */
  title: string;
  /** One line above the field explaining what will be written. */
  copy: string;
  placeholder: string;
  /** Prefilled brief, e.g. the article title the author already typed. */
  initialBrief?: string;
  onClose: () => void;
  onGenerate: (brief: string) => void;
  /** Credit cost of the pass this dialog starts; shown on its confirm once priced. */
  cost?: number;
};

/**
 * Asks the owner what the article should be about, then hands it to Lumio.
 *
 * The confirm stays disabled until the brief is long enough to be worth a model
 * call, and the hint under the field says how much more is needed rather than
 * leaving the author to guess at a dead button.
 *
 * The field is reset from `initialBrief` every time the dialog OPENS, not on
 * every render: an author who types over the prefilled title must keep what they
 * typed, and one who closes and reopens must get the prefill back.
 */
export const BriefModal = ({
  open,
  title,
  copy,
  placeholder,
  initialBrief = '',
  onClose,
  onGenerate,
  cost,
}: BriefModalProps): React.ReactElement => {
  const { t } = useTranslation();
  const [brief, setBrief] = React.useState(initialBrief);
  const fieldRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (!open) return;
    setBrief(initialBrief);
    // The dialog exists to be typed into, so the caret starts in the field. Done
    // on open rather than with `autoFocus`, which fires on mount and would move
    // the caret when the dialog is not even the thing on screen.
    fieldRef.current?.focus();
    // `initialBrief` deliberately absent: a title typed while the dialog is open
    // must not overwrite what the author is writing in it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = brief.trim();
  const missing = MIN_BRIEF_CHARS - trimmed.length;
  const ready = missing <= 0;

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={onClose} />
          <GenerateButton label="Generate" size="md" cost={cost} disabled={!ready} onClick={() => onGenerate(trimmed)} />
        </>
      }
    >
      <Copy text={copy} tag="span" />
      <BriefArea
        ref={fieldRef}
        value={brief}
        aria-label={t('What the article is about')}
        placeholder={t(placeholder)}
        onChange={(event) => setBrief(event.target.value)}
      />
      <Hint
        $short={!ready}
        text={
          ready
            ? t('Lumio has enough to go on.')
            : trimmed === ''
              ? t('Name the subject and anything it has to cover.')
              : t('Say a little more, at least {{missing}} more characters.', { missing })
        }
        tag="span"
      />
    </Modal>
  );
};

export default BriefModal;
